import { inject, Injectable } from '@angular/core';
import { WORKFLOW_STORAGE_STORE } from './workflow-storage-store.token';
import type {
  StoredWorkflowStorageRecord,
  WorkflowStorageDataKind,
  WorkflowStorageEnvelope,
  WorkflowStorageKind,
  WorkflowStorageOwner,
  WorkflowStoragePutOptions,
} from './workflow-storage.types';

/**
 * Версия формата хранения. Записи другой версии не читаются и удаляются —
 * старые данные не «оживают» после смены формата (в т.ч. после выключения фичи).
 */
export const WORKFLOW_STORAGE_VERSION = 1;

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

export const WORKFLOW_STORAGE_TTL_MS: Record<WorkflowStorageKind, number> = {
  'wf-data': DAY_MS,
  'wf-draft': 7 * DAY_MS,
  'wf-errors': 7 * DAY_MS,
  'wf-queue-submission': 7 * DAY_MS,
  'wf-queue-completion': 7 * DAY_MS,
};

const DATA_KINDS: readonly WorkflowStorageDataKind[] = [
  'wf-data',
  'wf-draft',
  'wf-errors',
];

/**
 * Хранилище регламентов с конвертом владельца (по образцу OfflineSnapshotRepository).
 * Ошибки IndexedDB не проглатываются: вызывающий код обязан сообщить пользователю,
 * что данные не сохранены локально.
 */
@Injectable({ providedIn: 'root' })
export class WorkflowStorageRepository {
  private readonly store = inject(WORKFLOW_STORAGE_STORE);

  async put<T>(
    owner: WorkflowStorageOwner,
    kind: WorkflowStorageKind,
    resourceKey: string,
    value: T,
    options: WorkflowStoragePutOptions,
  ): Promise<WorkflowStorageEnvelope<T>> {
    const now = Date.now();
    const key = createKey(owner, kind, resourceKey);
    // Обновление записи (попытки/статус задачи) не меняет её место в очереди:
    // createdAt сохраняется, иначе повторяемая задача окажется позади более новых
    const existing = await this.store.get(key);
    const createdAt =
      existing &&
      existing.environment === owner.environment &&
      existing.ownerUserId === owner.userId &&
      existing.kind === kind
        ? existing.createdAt
        : new Date(now).toISOString();
    const record: WorkflowStorageEnvelope<T> = {
      key,
      environment: owner.environment,
      ownerUserId: owner.userId,
      storageVersion: WORKFLOW_STORAGE_VERSION,
      kind,
      resourceKey,
      requestNumber: options.requestNumber,
      createdAt,
      expiresAt: new Date(now + WORKFLOW_STORAGE_TTL_MS[kind]).toISOString(),
      value,
    };

    if (options.workflowVersionId !== undefined) {
      record.workflowVersionId = options.workflowVersionId;
    }

    await this.store.put(record);

    return record;
  }

  async get<T>(
    owner: WorkflowStorageOwner,
    kind: WorkflowStorageKind,
    resourceKey: string,
  ): Promise<WorkflowStorageEnvelope<T> | null> {
    const key = createKey(owner, kind, resourceKey);
    const stored = await this.store.get(key);

    if (!stored) {
      return null;
    }

    if (
      !isUsableRecord(stored, owner, kind) ||
      stored.resourceKey !== resourceKey
    ) {
      await this.store.delete(key);

      return null;
    }

    return stored as WorkflowStorageEnvelope<T>;
  }

  async delete(
    owner: WorkflowStorageOwner,
    kind: WorkflowStorageKind,
    resourceKey: string,
  ): Promise<void> {
    await this.store.delete(createKey(owner, kind, resourceKey));
  }

  /**
   * Все действующие записи владельца указанного вида (просроченные и чужой версии удаляются).
   */
  async list<T>(
    owner: WorkflowStorageOwner,
    kind: WorkflowStorageKind,
    requestNumber?: string,
  ): Promise<WorkflowStorageEnvelope<T>[]> {
    const records = await this.store.entries();
    const result: WorkflowStorageEnvelope<T>[] = [];
    const toDelete: string[] = [];

    for (const record of records) {
      if (
        record.environment !== owner.environment ||
        record.ownerUserId !== owner.userId ||
        record.kind !== kind
      ) {
        continue;
      }

      if (!isUsableRecord(record, owner, kind)) {
        toDelete.push(record.key);
        continue;
      }

      if (
        requestNumber === undefined ||
        record.requestNumber === requestNumber
      ) {
        result.push(record as WorkflowStorageEnvelope<T>);
      }
    }

    await Promise.all(toDelete.map(key => this.store.delete(key)));

    return result.sort(
      (left, right) => Date.parse(left.createdAt) - Date.parse(right.createdAt),
    );
  }

  /**
   * Удалить кэш, черновики и ошибки владельца (logout / смена пользователя).
   * Очереди владельца не трогаются.
   */
  async clearOwnerData(owner: WorkflowStorageOwner): Promise<void> {
    const records = await this.store.entries();
    const ownedData = records.filter(
      record =>
        record.environment === owner.environment &&
        record.ownerUserId === owner.userId &&
        (DATA_KINDS as readonly string[]).includes(record.kind),
    );

    await Promise.all(ownedData.map(record => this.store.delete(record.key)));
  }
}

function createKey(
  owner: WorkflowStorageOwner,
  kind: WorkflowStorageKind,
  resourceKey: string,
): string {
  return `${owner.environment}:${owner.userId}:${kind}:${resourceKey}`;
}

function isUsableRecord(
  record: StoredWorkflowStorageRecord,
  owner: WorkflowStorageOwner,
  kind: WorkflowStorageKind,
): boolean {
  return (
    record.storageVersion === WORKFLOW_STORAGE_VERSION &&
    record.environment === owner.environment &&
    record.ownerUserId === owner.userId &&
    record.kind === kind &&
    Date.parse(record.expiresAt) > Date.now()
  );
}
